import {
  activeSupplements,
  daysAgo,
  fmtNum,
  partOrder,
  poMoney,
  poTotals,
  roDateTime,
  type Rfq,
  t,
} from '@procumi/shared';
import type { Route } from '../routes';

/**
 * Furnizorii aleși la comanda împărțită, câte un rând fiecare: ce poziții ia,
 * comanda lui și ce mai e de făcut. Ține locul barei „Ai ales” de la o cerere
 * cu un singur furnizor.
 */
export function SplitAward({
  rfq,
  canceled,
  go,
  onOrder,
  onSupplement,
  onEdit,
}: {
  rfq: Rfq;
  /** cererea e anulată: nu se mai fac comenzi */
  canceled: boolean;
  go: (r: Route) => void;
  onOrder: (supplierId: string) => void;
  onSupplement: (supplierId: string) => void;
  onEdit: () => void;
}) {
  const chosen = rfq.chosen!;
  const parts = chosen.split ?? [];
  const open = (orderId: string) => go({ view: 'order', rfqId: rfq.id, orderId });

  return (
    <section className="award sheet split-award" aria-label={t('Comanda împărțită')}>
      <div className="award-pick">
        <span className="award-k">{t('Comandă împărțită')}</span>
        <b className="award-v">
          {t(
            '{0} furnizori · {1} {2}',
            parts.length,
            rfq.items.length,
            rfq.items.length === 1 ? t('poziție') : t('poziții'),
          )}
        </b>
        <span className="award-s">
          {chosen.total == null ? '' : t('{0} lei fără TVA · ', fmtNum(chosen.total))}
          {chosen.by ? `${chosen.by.name}, ` : ''}
          {daysAgo(chosen.at)}
          {chosen.note ? ` · „${chosen.note}”` : ''}
        </span>
      </div>
      <div className="award-acts">
        {canceled ? null : (
          <button className="btn" onClick={onEdit}>
            {t('Schimbă împărțirea')}
          </button>
        )}
      </div>

      <ul className="split-parts">
        {parts.map((p) => {
          const issued = partOrder(rfq, p.supplier.id, 'issued');
          const pending = issued ? null : partOrder(rfq, p.supplier.id, 'pending');
          const sups = activeSupplements(rfq).filter((o) => o.supplier.id === p.supplier.id);
          const what = rfq.items.filter((it) => p.itemIds.includes(it.id)).map((it) => it.desc);
          return (
            <li key={p.supplier.id}>
              <div className="sp-who">
                <b>{p.supplier.name}</b>
                <span>{what.join(' · ')}</span>
              </div>
              <div className="sp-po">
                {issued ? (
                  <>
                    <b>{issued.number}</b>
                    <span>
                      {t(
                        'emisă {0} · {1} fără TVA{2}',
                        roDateTime(issued.issuedAt),
                        poMoney(poTotals(issued.data).net, issued.data.currency),
                        sups.length
                          ? ` · ${sups.length === 1 ? t('1 suplimentare') : t('{0} suplimentări', sups.length)}`
                          : '',
                      )}
                    </span>
                  </>
                ) : pending ? (
                  <>
                    <b>{pending.number}</b>
                    <span>{t('așteaptă aprobare')}</span>
                  </>
                ) : (
                  <>
                    <b className="muted-v">{t('Încă nefăcută')}</b>
                    <span>{t('Se completează din oferta lui, doar cu pozițiile de aici.')}</span>
                  </>
                )}
              </div>
              <div className="sp-acts">
                {issued ? (
                  <>
                    <button className="btn small primary" onClick={() => open(issued.id)}>
                      {t('Deschide comanda')}
                    </button>
                    <button className="btn small" onClick={() => onSupplement(p.supplier.id)}>
                      {t('Suplimentare')}
                    </button>
                  </>
                ) : pending ? (
                  <button className="btn small primary" onClick={() => open(pending.id)}>
                    {t('Deschide comanda')}
                  </button>
                ) : canceled ? null : (
                  <button className="btn small primary" onClick={() => onOrder(p.supplier.id)}>
                    {t('Fă comanda')}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
